import { Controller, useFormContext } from 'react-hook-form'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Select } from '@factorialco/f0-react'
import { ThumbsUp as ThumbsUpIcon } from '@factorialco/f0-react/icons/app'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { NodeUIApproval } from 'modules/workflows/components/Node/types'
import { ApprovalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/approvalFormSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

import { SidepanelFooter } from './partials/SidepanelFooter'
import { SidepanelHeader } from './partials/SidepanelHeader'
import { SidepanelWrapper } from './partials/SidepanelWrapper'

export type ApprovalNodeSidepanelSaveData = {
  title: string
  description: string
  handler_name: string
  payload: Record<string, unknown>
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/** When true, only Manual Review is shown; automatic approval and automatic rejection are excluded (for approvals inside multiAction). */
const isAutomaticApprovalHandler = (handlerName: string, label: string): boolean => {
  const n = (handlerName || '').toLowerCase()
  const l = (label || '').toLowerCase()
  return (
    n.includes('automatic') ||
    n.includes('reject') ||
    l.includes('automatic') ||
    l.includes('reject')
  )
}

/** Returns the first approval handler name that is Manual Review (not automatic). For multiAction default. */
export const getDefaultManualReviewHandlerName = (
  handlers: Array<{ name: string; actionGroup?: string }>,
  handlersConfig: Record<string, { dropDownLabel?: string }>
): string | undefined => {
  const approvalHandlers = handlers.filter((h) => h.name && h.actionGroup === 'approvals')
  for (const handler of approvalHandlers) {
    const label = handlersConfig[handler.name]?.dropDownLabel ?? handler.name
    if (!isAutomaticApprovalHandler(handler.name, label)) return handler.name
  }
  return undefined
}

type Props = {
  node: NodeUIApproval
  /** When provided (e.g. editing a parallel task inside multiAction), called instead of updating graph nodes. */
  onSave?: (data: ApprovalNodeSidepanelSaveData) => void
  /** When provided, called instead of deselecting on cancel (e.g. go back to multiAction list). */
  onCancel?: () => void
  /** When provided, show back arrow in header; click returns to Multi Action sidepanel. */
  onBack?: () => void
  /** When true, only Manual Review is available; automatic approval/rejection are hidden (for multiAction context). */
  restrictToManualReviewOnly?: boolean
  readonly?: boolean
}
export const ApprovalNodeSidepanel = ({
  node,
  onSave,
  onCancel,
  onBack,
  restrictToManualReviewOnly = false,
  readonly = false,
}: Props) => {
  const { workflowManifest, handlersConfig, setNodesUI, workflow } = useWorkflowContext()
  const { handleSubmit, getValues, control, watch, setValue } = useFormContext<ApprovalFormSchema>()

  const handlerName = watch('nodeData.handlerType')
  const payloadValue = watch('nodeData.payload')
  const handlerForm = handlersConfig[handlerName]
  const HandlerFormComponent = handlerForm?.component

  useUnsafeEffect(() => {
    //When we change the handler type the form payload must be reset to the default value
    const currentHandlerForm = handlersConfig[handlerName]
    const payloadData = currentHandlerForm?.initializer?.(node.data.payload)

    setValue('nodeData.payload', payloadData || {})
  }, [handlerName])

  const approvalTypes = workflowManifest.handlers
    .filter((handler) => handler.actionGroup === 'approvals')
    .map((type) => ({
      value: type.name,
      label: handlersConfig[type.name]?.dropDownLabel || type.name,
    }))
    .filter(
      (option) =>
        !restrictToManualReviewOnly || !isAutomaticApprovalHandler(option.value, option.label)
    )

  useUnsafeEffect(() => {
    if (!restrictToManualReviewOnly || approvalTypes.length === 0) return
    const allowedValues = approvalTypes.map((o) => o.value)
    const firstManual = approvalTypes[0]?.value
    const shouldSetDefault = firstManual && (!handlerName || !allowedValues.includes(handlerName))
    if (shouldSetDefault) setValue('nodeData.handlerType', firstManual)
  }, [restrictToManualReviewOnly, approvalTypes, handlerName, setValue])

  // Closes form by deselecting all nodes and clearing suppressInitialValidation
  const handleCancel = () => {
    if (onCancel) {
      onCancel()
      return
    }
    setNodesUI((prev) =>
      prev.map((n) => {
        const updated = { ...n, selected: false }
        if (n.id === node.id) {
          updated.data = { ...n.data, suppressInitialValidation: false }
        }
        return updated
      })
    )
  }

  const deriveApprovalDescription = (handlerTypeName: string): string => {
    const normalized = (handlerTypeName || '').toLowerCase()
    if (normalized.includes('reject')) {
      return i18n.t('workflows.approval.types.auto_reject_description')
    } else if (normalized.includes('approve')) {
      return i18n.t('workflows.approval.types.auto_approve_description')
    }
    return i18n.t('workflows.approval.types.request_review_description')
  }

  // Updates global state and deselects all nodes (or calls onSave when editing a parallel task)
  const handleSave = () => {
    const { nodeData } = getValues()

    const handlerLabel =
      handlersConfig[nodeData.handlerType]?.dropDownLabel ||
      i18n.t('workflows.building_blocks.approval.title')

    const resolvedTitle = handlerLabel
    const resolvedDescription = deriveApprovalDescription(nodeData.handlerType)

    const beforeSaveData = handlerForm?.beforeSave
    const resolvedPayload = beforeSaveData
      ? beforeSaveData(nodeData.payload, nodeData.handlerType, {
          nodeId: node.id,
          workflow,
        })
      : nodeData.payload

    if (onSave) {
      onSave({
        title: resolvedTitle,
        description: resolvedDescription,
        handler_name: nodeData.handlerType,
        payload: isRecord(resolvedPayload) ? resolvedPayload : {},
      })
      return
    }

    setNodesUI((prev) =>
      prev.map((n) => {
        if (n.id === node.id) {
          return {
            ...node,
            data: {
              ...node.data,
              title: resolvedTitle,
              description: resolvedDescription,
              handler_name: nodeData.handlerType,
              payload: resolvedPayload,
              suppressInitialValidation: false,
            },
            selected: false,
          }
        }
        return { ...n, selected: false }
      })
    )
  }

  const onSubmit = () => {
    handleSubmit(handleSave, (_fields) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('workflows.sidepanel.approval.validation.generic_error'),
        time: 3000,
      })
    })()
  }

  return (
    <SidepanelWrapper
      header={
        <SidepanelHeader
          nodeId={node.id}
          backgroundColor="#6156ED20"
          icon={
            <ThumbsUpIcon width={22} height={22} style={{ color: '#6156ED', strokeWidth: 1.5 }} />
          }
          title={i18n.t('workflows.sidepanel.approval.edit_title')}
          onClose={handleCancel}
          onBack={onBack}
          showDeleteButton={!readonly}
          showDuplicateButton={!readonly}
        />
      }
      footer={
        readonly ? undefined : <SidepanelFooter onCancel={handleCancel} onSave={onSubmit} isValid />
      }
    >
      <Box gap="s16">
        <Text color="textPrimary" size="300" weight="medium">
          {i18n.t('workflows.sidepanel.approval.title')}
        </Text>

        <Controller
          name="nodeData.handlerType"
          control={control}
          render={({ field, fieldState }) => (
            <F0Select
              size="md"
              label={i18n.t('workflows.sidepanel.general.type')}
              placeholder={i18n.t('workflows.sidepanel.general.type_placeholder')}
              value={field.value}
              onChange={field.onChange}
              options={approvalTypes}
              error={fieldState.error?.message}
              required
              disabled={readonly}
            />
          )}
        />
      </Box>

      {HandlerFormComponent !== undefined && (
        <Box gap="s16">
          <HandlerFormComponent
            payload={isRecord(payloadValue) ? payloadValue : {}}
            onChange={(values) => {
              setValue('nodeData.payload', isRecord(values) ? values : {})
            }}
            readonly={readonly}
          />
        </Box>
      )}
    </SidepanelWrapper>
  )
}
